import { Reducer } from 'react';
import { Active, Over } from '@dnd-kit/core';

import {
    UILayout,
    UIElement,
    UIElementType,
    Description,
    EditorUILayout,
    EditorUIElement,
    EditorUIContainer,
    FormType,
    RecordGroup,
    FormSplitConfiguration,
    VisibilityTypeId,
    UIArTableColumnOverride,
    WorkflowMenuSection,
} from '#types/System.types';
import { FormDeriveEntry } from '#types';
import { FormStateId, CONTAINER_ELEMENT_TYPES, VALUE_ELEMENT_TYPES } from '#types/System.Parameters.types';
import { normalizeLayoutColumnConfig } from '../components/editor/columnConfigNormalization';


/**
 * Maximum number of past states to store in the history
 * This is a trade-off between memory usage and the ability to undo/redo
 * multiple actions.
 */
const MAX_HISTORY_SIZE = 25;

/**
 * State interface for managing the layout editor's state.
 */
export interface LayoutEditorState {
    /** Currently selected element in the editor */
    selectedElement: EditorUIElement | null;
    /** Current layout being edited */
    layout: EditorUILayout;
    /** Description of the layout */
    description: Description;
    /** Form states configuration */
    possibleStates: FormType['possibleStates'];
    /** Allowed next form types */
    allowedNextForms: FormType['allowedNextForms'];
    /** Split configuration for entity-based form filtering */
    splitConfiguration?: FormSplitConfiguration;
    /** Protected fields and entity references configuration */
    protected?: FormType['protected'];
    /**
     * Derived-value configuration draft for the form type. Rides the same
     * history stacks as every other member, so the toolbar's Save, Undo and
     * Redo need no state of their own.
     */
    formDerives?: FormDeriveEntry[];
    /** Record groups for entity types (optional) */
    recordGroups?: { [key: string]: RecordGroup };
    /** Undo/redo history stacks */
    history: {
        past: Array<HistoryEntry>;
        future: Array<HistoryEntry>;
    };
}

/**
 * Represents a snapshot of editor state saved in history
 */
type HistoryEntry = {
    layout: EditorUILayout;
    possibleStates: FormType['possibleStates'];
    allowedNextForms: FormType['allowedNextForms'];
    splitConfiguration?: FormSplitConfiguration;
    protected?: FormType['protected'];
    formDerives?: FormDeriveEntry[];
    description: Description;
    selectedElement: EditorUIElement | null;
    recordGroups: { [key: string]: RecordGroup };
};

/**
 * Checks if a UI element type is a container type (Grid, Row, or Column).
 * Used to determine if elements can be dropped into it during drag and drop operations.
 * 
 * @param elementType - The type of UI element to check
 * @returns True if the element type is a container type
 */
const isContainerType = (elementType: UIElementType): boolean => 
    CONTAINER_ELEMENT_TYPES.includes(elementType);

/**
 * Assigns unique dndIds to all elements in a layout recursively.
 * Used when initializing a new layout or selecting a form type.
 * 
 * @param layout - The layout to process
 * @returns A new layout with dndIds assigned to all elements
 */
const assignElementIds = (layout: UILayout | undefined | null): EditorUILayout => {
    let counter = 0;

    const traverse = (element: UIElement): EditorUIElement => {
        if (CONTAINER_ELEMENT_TYPES.includes(element.uiElementType)) {
            const containerElement = element as any;
            return {
                ...containerElement,
                dndId: `e${counter++}`,
                children: containerElement.children ? containerElement.children.map(traverse) : []
            } as EditorUIElement;
        }

        return {
            ...element,
            dndId: `e${counter++}`
        } as EditorUIElement;
    };

    if (!layout) {
        console.warn('Layout is undefined. Creating default layout with container structure.');
        return {
            structure: {
                uiElementType: UIElementType.Column,
                children: [],
                dndId: `e${counter++}`
            } as EditorUIContainer
        } as EditorUILayout;
    }

    const normalizedLayout = normalizeLayoutColumnConfig(layout);

    if (!normalizedLayout.structure) {
        console.warn('Layout structure is undefined. Creating default container structure.');
        return {
            ...normalizedLayout,
            structure: {
                uiElementType: UIElementType.Column,
                children: [],
                dndId: `e${counter++}`
            } as EditorUIContainer
        };
    }

    return {
        ...normalizedLayout,
        structure: traverse(normalizedLayout.structure)
    };
};

/**
 * Finds a container element by its dndId in the layout structure.
 * Used during drag and drop operations to locate source and target containers.
 * 
 * @param layout - The layout to search in
 * @param containerId - The dndId of the container to find
 * @returns The found container or null if not found
 */
const findContainerById = (layout: EditorUILayout, containerId: string): EditorUIContainer | null => {
    const traverse = (element: EditorUIElement): EditorUIContainer | null => {
        if (element.dndId === containerId) {
            return element as EditorUIContainer;
        }
        
        if ('children' in element && element.children) {
            for (const child of element.children) {
                const result = traverse(child);
                if (result) return result;
            }
        }
        return null;
    };
    
    return traverse(layout.structure);
};

/**
 * Finds an element by its dndId in the layout structure.
 * Used during drag and drop operations to locate elements.
 * 
 * @param layout - The layout to search in
 * @param elementId - The dndId of the element to find
 * @returns The found element or null if not found
 */
export const findElementById = (layout: EditorUILayout, elementId: string): EditorUIElement | null => {
    const traverse = (element: EditorUIElement): EditorUIElement | null => {
        if (element.dndId === elementId) {
            return element;
        }
        
        if ('children' in element && element.children) {
            for (const child of element.children) {
                const result = traverse(child);
                if (result) return result;
            }
        }
        return null;
    };
    
    return traverse(layout.structure);
};

/**
 * Union type for all possible actions that can be dispatched to the layout editor reducer.
 * Each action type corresponds to a specific operation in the layout editor.
 */
export type LayoutEditorAction =
    /** Selects and initializes a new layout */
    | { type: 'SELECT_LAYOUT'; payload: UILayout }
    /** Handles drag and drop operations between containers */
    | { type: 'DROP_ELEMENT'; payload: { active: Active; over: Over }}
    /** Updates the currently selected element */
    | { type: 'SELECT_ELEMENT'; payload: EditorUIElement | null }
    /** Removes an element from its container */
    | { type: 'REMOVE_ELEMENT'; payload: {
        container: EditorUIContainer;
        elementDndId: string;
      }}
    /** Adds a new element to a container */
    | { type: 'ADD_ELEMENT'; payload: {
        element: EditorUIElement;
        container?: EditorUIContainer;
      }}
    /** Updates a field's property */
    | { type: 'UPDATE_FIELD_PROPERTY'; payload: {
        elementDndId: string;
        property: 'required';
        value: boolean;
      }}
    /** Sets an element's shown-in allow-list; `undefined` removes the key, restoring "shown in every view" */
    | { type: 'UPDATE_VISIBILITY_TYPES'; payload: {
        elementDndId: string;
        visibilityTypes: VisibilityTypeId[] | undefined;
      }}
    /** Updates a field's always required status */
    | { type: 'UPDATE_ALWAYS_REQUIRED'; payload: {
        elementDndId: string;
        alwaysRequired: boolean;
      }}
    /** Updates a field's internal-only status */
    | { type: 'UPDATE_INTERNAL_ONLY'; payload: {
        elementDndId: string;
        internalVisibilityTag: boolean;
      }}
    /** Updates a field's trigger search sync status */
    | { type: 'UPDATE_TRIGGER_SEARCH_SYNC'; payload: {
        elementDndId: string;
        triggerSearchSync: boolean;
      }}
    /** Updates a button element's Workflow-menu configuration */
    | { type: 'UPDATE_BUTTON_CONFIG'; payload: {
        elementDndId: string;
        menuSection?: WorkflowMenuSection;
        menuItemKey?: string;
        requiresConfirmation?: boolean;
        label?: string;
      }}
    /** Bulk update required status for multiple elements */
    | { type: 'BULK_UPDATE_REQUIRED'; payload: {
        updates: Array<{ elementDndId: string; required: boolean }>;
      }}
    /** Bulk update visibility types for multiple elements, one allow-list per element, in one history entry */
    | { type: 'BULK_UPDATE_VISIBILITY'; payload: {
        updates: Array<{ elementDndId: string; visibilityTypes: VisibilityTypeId[] | undefined }>;
      }}
    /** Reverts to the previous layout state */
    | { type: 'UNDO' }
    /** Reapplies a previously undone layout state */
    | { type: 'REDO' }
    /** Updates the description of the selected form type */
    | { type: 'UPDATE_DESCRIPTION'; payload: Description }
    /** Updates record groups for entity types */
    | { type: 'UPDATE_RECORD_GROUPS'; payload: { [key: string]: RecordGroup } }
    /** Updates the split configuration for entity-based form filtering */
    | { type: 'UPDATE_SPLIT_CONFIGURATION'; payload: FormSplitConfiguration | undefined }
    /** Replaces the whole derived-value draft for the form type */
    | { type: 'UPDATE_FORM_DERIVES'; payload: { formDerives: FormDeriveEntry[] } }
    /** Updates protected field or entity reference */
    | { type: 'UPDATE_PROTECTED'; payload: {
        fieldId?: string;
        entityReferenceId?: string;
        isProtected: boolean;
      }}
    /** Updates the form states configuration */
    | { type: 'FORM_STATES_CONFIG'; payload: {
        type: 'load';
        possibleStates: FormType['possibleStates'];
        allowedNextForms: FormType['allowedNextForms'];
      } | {
        type: 'update';
        states: FormStateId[];
      } | {
        type: 'updateNextStates';
        stateId: FormStateId;
        allowedNextStates: FormStateId[];
      } | {
        type: 'updateNextForms';
        forms: FormType['docId'][];
      }}
    /** Resets the layout state to initial state */
    | { type: 'RESET_LAYOUT' }
    /** Marks the current state as saved */
    | { type: 'SAVE_COMPLETE' }
    /** Selects and initializes a new structure with its layout and states */
    | { type: 'UPDATE_COLUMN_CONFIG'; payload: {
        elementDndId: string;
        columnConfig: UIArTableColumnOverride[] | undefined;
      }}
    | { type: 'SELECT_STRUCTURE'; payload: {
        layout: UILayout;
        description?: Description;
        possibleStates?: FormType['possibleStates'];
        allowedNextForms?: FormType['allowedNextForms'];
        splitConfiguration?: FormSplitConfiguration;
        protected?: FormType['protected'];
        formDerives?: FormDeriveEntry[];
        recordGroups?: { [key: string]: RecordGroup };
    }};

/**
 * Initial state for the layout editor.
 * Creates an empty column container as the root element.
 */
export const initialLayoutEditorState: LayoutEditorState = {
    selectedElement: null,
    description: {} as Description,
    layout: {
        structure: {
            uiElementType: UIElementType.Column,
            children: [],
            dndId: 'e0'
        } as EditorUIContainer,
    },
    possibleStates: {
        draft: {
            allowedNextStates: ['draft']
        }
    },
    allowedNextForms: [],
    recordGroups: {},
    history: {
        past: [],
        future: []
    }
};

/**
 * Creates a snapshot of the current editor state for history tracking
 */
const createStateSnapshot = (state: LayoutEditorState): HistoryEntry => ({
    layout: state.layout,
    possibleStates: state.possibleStates,
    allowedNextForms: state.allowedNextForms,
    splitConfiguration: state.splitConfiguration,
    protected: state.protected,
    formDerives: state.formDerives,
    description: state.description,
    selectedElement: state.selectedElement,
    recordGroups: state.recordGroups || {}
});

/**
 * Writes an element's shown-in allow-list. `undefined` omits the key rather than
 * storing it, because an absent `visibilityTypes` is what "shown in every view"
 * means — assigning `undefined` would persist a dead key on every element the
 * matrix ever cleared.
 */
const withVisibilityTypes = (
    element: EditorUIElement,
    visibilityTypes: VisibilityTypeId[] | undefined
): EditorUIElement => {
    if (visibilityTypes === undefined) {
        const { visibilityTypes: _cleared, ...rest } =
            element as EditorUIElement & { visibilityTypes?: VisibilityTypeId[] };
        return rest as EditorUIElement;
    }
    return { ...element, visibilityTypes } as EditorUIElement;
};

/**
 * Creates a new history object with size limits applied
 * @param past Current past history stack
 * @param newState State snapshot to add
 * @returns New history object with limited size
 */
const createLimitedHistory = (past: Array<HistoryEntry>, newState: HistoryEntry) => {
  const limitedPast = past.length >= MAX_HISTORY_SIZE
    ? past.slice(1)
    : past;
    
  return {
    past: [...limitedPast, newState],
    future: []
  };
};

/**
 * Reducer function for managing layout editor state.
 * Handles all layout modifications, element selection, and history management.
 * 
 * Key features:
 * - Maintains undo/redo history
 * - Handles drag and drop operations
 * - Manages element selection
 * - Processes layout modifications
 * 
 * @param state - Current state of the layout editor
 * @param action - Action to process
 * @returns New state after processing the action
 */
export const layoutEditorReducer: Reducer<LayoutEditorState, LayoutEditorAction> = (
    state: LayoutEditorState,
    action: LayoutEditorAction
): LayoutEditorState => {
    switch (action.type) {
        case 'SELECT_LAYOUT':
            return {
                ...state,
                layout: assignElementIds(action.payload),
                selectedElement: null,
                history: {
                    past: [],
                    future: []
                }
            };

        case 'FORM_STATES_CONFIG': {
            switch (action.payload.type) {
                case 'load':
                    return {
                        ...state,
                        possibleStates: action.payload.possibleStates,
                        allowedNextForms: action.payload.allowedNextForms || [],
                        history: {
                            past: [],
                            future: []
                        }
                    };
                    
                case 'update': {
                    const newPossibleStates: FormType['possibleStates'] = {
                        draft: state.possibleStates.draft 
                    };
                    
                    action.payload.states.forEach(stateId => {
                        if (stateId === 'draft') return;
                        newPossibleStates[stateId] = state.possibleStates[stateId] || {
                            allowedNextStates: []
                        };
                    });
                    
                    return {
                        ...state,
                        possibleStates: newPossibleStates,
                        history: createLimitedHistory(state.history.past, createStateSnapshot(state))
                    };
                }
                case 'updateNextStates':
                    return {
                        ...state,
                        possibleStates: {
                            ...state.possibleStates,
                            [action.payload.stateId]: {
                                ...state.possibleStates[action.payload.stateId],
                                allowedNextStates: action.payload.allowedNextStates
                            }
                        },
                        history: createLimitedHistory(state.history.past, createStateSnapshot(state))
                    };
                case 'updateNextForms':
                    return {
                        ...state,
                        allowedNextForms: action.payload.forms,
                        history: createLimitedHistory(state.history.past, createStateSnapshot(state))
                    };
                default:
                    return state;
            }
        }

        case 'RESET_LAYOUT':
            return initialLayoutEditorState;

        case 'DROP_ELEMENT': {
            const { active, over } = action.payload;
            if (!over) return state;

            const placement = over.data.current?.placement;
            const targetElementId = over.data.current?.targetElementId;
            const parentId = over.data.current?.parentId;

            const newLayout = structuredClone(state.layout);

            const sourceContainer = findContainerById(
                newLayout,
                active.data.current?.container
            );
            let targetContainer: EditorUIContainer | null = null;
            if (placement === 'inside') {
                targetContainer = findContainerById(newLayout, targetElementId);
            } else {
                targetContainer = findContainerById(
                    newLayout,
                    parentId || active.data.current?.container
                );
            }
            if (!sourceContainer || !targetContainer) return state;

            const elementToMove = findElementById(newLayout, active.id.toString());
            if (!elementToMove) return state;

            sourceContainer.children = sourceContainer.children.filter(
                (child: EditorUIElement) => child.dndId !== elementToMove.dndId
            );

            let insertIndex: number;
            if (placement === 'inside') {
                insertIndex = over.data.current?.sortable?.index ?? targetContainer.children.length;
            } else {
                const idx = targetContainer.children.findIndex(
                    (c: EditorUIElement) => c.dndId === targetElementId
                );
                insertIndex = idx >= 0
                    ? (placement === 'after' ? idx + 1 : idx)
                    : targetContainer.children.length;
            }
            targetContainer.children = [
                ...targetContainer.children.slice(0, insertIndex),
                elementToMove,
                ...targetContainer.children.slice(insertIndex)
            ] as EditorUIElement[];

            return {
                ...state,
                layout: newLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'REMOVE_ELEMENT': {
            const { container, elementDndId } = action.payload;
            const newLayout = structuredClone(state.layout);
            
            const targetContainer = findContainerById(newLayout, container.dndId);
            if (targetContainer) {
                const elementIndex = targetContainer.children.findIndex(
                    (child: EditorUIElement) => child.dndId === elementDndId
                );
                if (elementIndex !== -1) {
                    targetContainer.children.splice(elementIndex, 1);
                }
            }

            return {
                ...state,
                selectedElement: null,
                layout: newLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'SELECT_ELEMENT':
            return {
                ...state,
                selectedElement: action.payload
            };

        case 'ADD_ELEMENT': {
            const { element, container } = action.payload;
            const newLayout = structuredClone(state.layout);
            const isContainer = isContainerType(element.uiElementType);

            if (container) {
                const targetContainer = findContainerById(newLayout, container.dndId);
                if (targetContainer) {
                    targetContainer.children = targetContainer.children || [];
                    targetContainer.children.unshift(element);
                }
            } 
            else if (state.selectedElement) {
                if (isContainer) {
                    const selectedContainer = findContainerById(newLayout, state.selectedElement.dndId);
                    if (selectedContainer) {
                        selectedContainer.children = selectedContainer.children || [];
                        selectedContainer.children.unshift(element);
                    }
                } else {
                    const traverse = (container: EditorUIContainer): boolean => {
                        if (!container.children) {
                            container.children = [];
                        }

                        const index = container.children.findIndex(child =>
                            (child as EditorUIElement).dndId === state.selectedElement?.dndId
                        );

                        if (index !== -1) {
                            container.children.splice(index + 1, 0, element);
                            return true;
                        }

                        return container.children.some(child =>
                            isContainerType(child.uiElementType) && traverse(child as EditorUIContainer)
                        );
                    };

                    if (newLayout.structure) {
                        traverse(newLayout.structure as EditorUIContainer);
                    } else {
                        console.warn('Layout structure is undefined in ADD_ELEMENT. Cannot add element.');
                    }
                }
            }
            else {
                if (!newLayout.structure) {
                    console.warn('Layout structure is undefined. Creating default container structure.');
                    newLayout.structure = {
                        uiElementType: UIElementType.Column,
                        children: [],
                        dndId: 'root'
                    } as EditorUIContainer;
                }
                const rootContainer = newLayout.structure as EditorUIContainer;
                if (!rootContainer.children) {
                    rootContainer.children = [];
                }
                rootContainer.children.unshift(element);
            }
            
            return {
                ...state,
                layout: newLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'UPDATE_FIELD_PROPERTY': {
            const { elementDndId, property, value } = action.payload;
            if (property !== 'required' || typeof value !== 'boolean') {
                return state;
            }

            const updateElement = (element: EditorUIElement): EditorUIElement => {
                if (element.dndId === elementDndId) {
                    const isField = element.uiElementType === UIElementType.Field;
                    const isEntityReference = element.uiElementType === UIElementType.EntityReference;
                    const isEmbeddedEntity = element.uiElementType === UIElementType.EmbeddedEntity;
                    const isAttachments = element.uiElementType === UIElementType.Attachments
                        || element.uiElementType === UIElementType.ReferencedList;
                    const isMetaField = element.uiElementType === UIElementType.MetaField;
                    const isFormProp = element.uiElementType === UIElementType.FormProperty;
                    if (isField || isEntityReference || isEmbeddedEntity || isAttachments || isMetaField || isFormProp) {
                        return {
                            ...element,
                            required: value
                        };
                    }
                    return element;
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElement)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElement(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'UPDATE_BUTTON_CONFIG': {
            const { elementDndId, ...patch } = action.payload;

            const updateElement = (element: EditorUIElement): EditorUIElement => {
                if (element.dndId === elementDndId) {
                    if (element.uiElementType !== UIElementType.Button) {
                        return element;
                    }
                    const next = { ...element } as EditorUIElement & {
                        menuSection?: WorkflowMenuSection;
                        menuItemKey?: string;
                        requiresConfirmation?: boolean;
                        label?: string;
                    };
                    if ('menuSection' in patch) {
                        if (patch.menuSection) next.menuSection = patch.menuSection;
                        else {
                            delete next.menuSection;
                            delete next.menuItemKey;
                        }
                    }
                    if ('menuItemKey' in patch) {
                        if (patch.menuItemKey) next.menuItemKey = patch.menuItemKey;
                        else delete next.menuItemKey;
                    }
                    if ('requiresConfirmation' in patch) {
                        if (patch.requiresConfirmation) next.requiresConfirmation = true;
                        else delete next.requiresConfirmation;
                    }
                    if ('label' in patch) {
                        if (patch.label) next.label = patch.label;
                        else delete next.label;
                    }
                    return next;
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElement)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElement(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'UPDATE_VISIBILITY_TYPES': {
            const { elementDndId, visibilityTypes } = action.payload;

            const updateElement = (element: EditorUIElement): EditorUIElement => {
                if (element.dndId === elementDndId) {
                    if (VALUE_ELEMENT_TYPES.includes(element.uiElementType)) {
                        return withVisibilityTypes(element, visibilityTypes);
                    }
                    return element;
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElement)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElement(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'UPDATE_ALWAYS_REQUIRED': {
            const { elementDndId, alwaysRequired } = action.payload;

            const updateElement = (element: EditorUIElement): EditorUIElement => {
                if (element.dndId === elementDndId) {
                    const isField = element.uiElementType === UIElementType.Field;
                    const isEntityReference = element.uiElementType === UIElementType.EntityReference;
                    const isEmbeddedEntity = element.uiElementType === UIElementType.EmbeddedEntity;
                    const isAttachments = element.uiElementType === UIElementType.Attachments
                        || element.uiElementType === UIElementType.ReferencedList;
                    const isMetaField = element.uiElementType === UIElementType.MetaField;
                    const isFormProp = element.uiElementType === UIElementType.FormProperty;
                    if (isField || isEntityReference || isEmbeddedEntity || isAttachments || isMetaField || isFormProp) {
                        return {
                            ...element,
                            alwaysRequired
                        } as EditorUIElement;
                    }
                    return element;
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElement)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElement(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'UPDATE_INTERNAL_ONLY': {
            const { elementDndId, internalVisibilityTag } = action.payload;

            const updateElement = (element: EditorUIElement): EditorUIElement => {
                if (element.dndId === elementDndId) {
                    const isField = element.uiElementType === UIElementType.Field;
                    const isEntityReference = element.uiElementType === UIElementType.EntityReference;
                    const isEmbeddedEntity = element.uiElementType === UIElementType.EmbeddedEntity;
                    const isAttachments = element.uiElementType === UIElementType.Attachments
                        || element.uiElementType === UIElementType.ReferencedList;
                    const isMetaField = element.uiElementType === UIElementType.MetaField;
                    const isFormProp = element.uiElementType === UIElementType.FormProperty;
                    const isTable = element.uiElementType === UIElementType.Table;
                    if (isField || isEntityReference || isEmbeddedEntity || isAttachments || isMetaField || isFormProp || isTable) {
                        return {
                            ...element,
                            internalVisibilityTag
                        } as EditorUIElement;
                    }
                    return element;
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElement)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElement(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'UPDATE_TRIGGER_SEARCH_SYNC': {
            const { elementDndId, triggerSearchSync } = action.payload;

            const updateElement = (element: EditorUIElement): EditorUIElement => {
                if (element.dndId === elementDndId) {
                    const isField = element.uiElementType === UIElementType.Field;
                    const isEntityReference = element.uiElementType === UIElementType.EntityReference;
                    const isEmbeddedEntity = element.uiElementType === UIElementType.EmbeddedEntity;
                    if (isField || isEntityReference || isEmbeddedEntity) {
                        return {
                            ...element,
                            triggerSearchSync
                        } as EditorUIElement;
                    }
                    return element;
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElement)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElement(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'BULK_UPDATE_REQUIRED': {
            const { updates } = action.payload;
            
            const updateElements = (element: EditorUIElement): EditorUIElement => {
                const update = updates.find(u => u.elementDndId === element.dndId);
                if (update) {
                    const isField = element.uiElementType === UIElementType.Field;
                    const isEntityReference = element.uiElementType === UIElementType.EntityReference;
                    const isEmbeddedEntity = element.uiElementType === UIElementType.EmbeddedEntity;
                    const isAttachments = element.uiElementType === UIElementType.Attachments
                        || element.uiElementType === UIElementType.ReferencedList;
                    const isMetaField = element.uiElementType === UIElementType.MetaField;
                    const isFormProp = element.uiElementType === UIElementType.FormProperty;
                    if (isField || isEntityReference || isEmbeddedEntity || isAttachments || isMetaField || isFormProp) {
                        return {
                            ...element,
                            required: update.required
                        };
                    }
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElements)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElements(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'BULK_UPDATE_VISIBILITY': {
            const { updates } = action.payload;

            const updateElements = (element: EditorUIElement): EditorUIElement => {
                const update = updates.find(u => u.elementDndId === element.dndId);
                if (update) {
                    if (VALUE_ELEMENT_TYPES.includes(element.uiElementType)) {
                        return withVisibilityTypes(element, update.visibilityTypes);
                    }
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElements)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElements(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'UPDATE_COLUMN_CONFIG': {
            const { elementDndId, columnConfig } = action.payload;

            const updateElement = (element: EditorUIElement): EditorUIElement => {
                if (element.dndId === elementDndId) {
                    if (element.uiElementType === UIElementType.Table) {
                        return {
                            ...element,
                            columnConfig
                        } as EditorUIElement;
                    }
                    return element;
                }

                if ('children' in element && element.children) {
                    return {
                        ...element,
                        children: element.children.map(updateElement)
                    };
                }

                return element;
            };

            const updatedLayout = {
                ...state.layout,
                structure: updateElement(state.layout.structure)
            };

            return {
                ...state,
                layout: updatedLayout,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'UNDO': {
            if (state.history.past.length === 0) return state;
            const previous = state.history.past[state.history.past.length - 1];
            return {
                ...state,
                layout: previous.layout,
                possibleStates: previous.possibleStates,
                allowedNextForms: previous.allowedNextForms,
                splitConfiguration: previous.splitConfiguration,
                protected: previous.protected,
                formDerives: previous.formDerives,
                description: previous.description,
                selectedElement: previous.selectedElement,
                recordGroups: previous.recordGroups,
                history: {
                    past: state.history.past.slice(0, -1),
                    future: [createStateSnapshot(state), ...state.history.future]
                }
            };
        }

        case 'REDO': {
            if (state.history.future.length === 0) return state;
            const next = state.history.future[0];
            return {
                ...state,
                layout: next.layout,
                possibleStates: next.possibleStates,
                allowedNextForms: next.allowedNextForms,
                splitConfiguration: next.splitConfiguration,
                protected: next.protected,
                formDerives: next.formDerives,
                description: next.description,
                selectedElement: next.selectedElement,
                recordGroups: next.recordGroups,
                history: {
                    past: [...state.history.past, createStateSnapshot(state)],
                    future: state.history.future.slice(1)
                }
            };
        }

        case 'UPDATE_DESCRIPTION':
            return {
                ...state,
                description: {
                    ...state.description,
                    ...action.payload
                },
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };

        case 'UPDATE_RECORD_GROUPS':
            return {
                ...state,
                recordGroups: action.payload,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };

        case 'UPDATE_SPLIT_CONFIGURATION':
            return {
                ...state,
                splitConfiguration: action.payload,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };

        case 'UPDATE_FORM_DERIVES':
            return {
                ...state,
                formDerives: action.payload.formDerives,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };

        case 'UPDATE_PROTECTED': {
            const { fieldId, entityReferenceId, isProtected } = action.payload;
            const currentProtected = state.protected || {};
            const currentFieldIds = new Set(currentProtected.protectedFieldIds || []);
            const currentEntityRefIds = new Set(currentProtected.protectedEntityReferenceIds || []);

            if (fieldId) {
                if (isProtected) {
                    currentFieldIds.add(fieldId);
                } else {
                    currentFieldIds.delete(fieldId);
                }
            }
            if (entityReferenceId) {
                if (isProtected) {
                    currentEntityRefIds.add(entityReferenceId);
                } else {
                    currentEntityRefIds.delete(entityReferenceId);
                }
            }

            const newProtected = (currentFieldIds.size > 0 || currentEntityRefIds.size > 0)
                ? {
                    protectedFieldIds: currentFieldIds.size > 0 ? Array.from(currentFieldIds) : undefined,
                    protectedEntityReferenceIds: currentEntityRefIds.size > 0 ? Array.from(currentEntityRefIds) : undefined,
                }
                : undefined;

            return {
                ...state,
                protected: newProtected,
                history: createLimitedHistory(state.history.past, createStateSnapshot(state))
            };
        }

        case 'SAVE_COMPLETE':
            return {
                ...state,
                history: {
                    past: [],
                    future: []
                }
            };

        case 'SELECT_STRUCTURE':
            return {
                ...initialLayoutEditorState,
                layout: assignElementIds(action.payload?.layout),
                description: action.payload?.description || {},
                possibleStates: action.payload?.possibleStates || {
                    draft: {
                        allowedNextStates: ['draft']
                    }
                },
                allowedNextForms: action.payload?.allowedNextForms || [],
                splitConfiguration: action.payload?.splitConfiguration,
                protected: action.payload?.protected,
                formDerives: action.payload?.formDerives,
                recordGroups: action.payload?.recordGroups || {},
                history: {
                    past: [],
                    future: []
                }
            };

        default:
            return state;
    }
}; 